import './SourceCards.css';

// Perplexity-style source cards for a web-search answer. Pure presentation — the
// backend sends the structured `sources` array (via the brew.sources SSE event),
// this owns the LOOK. No styling lives on the backend.

export interface SourceItem {
  n: number;
  title?: string;
  url?: string;
  domain?: string;
  publishedAt?: string;
}

// Letter-avatar colours — deterministic per domain, so a given outlet always gets
// the same chip colour. Avoids an external favicon request (CSP-safe, no network).
const AVATAR = ['#e06c75', '#61afef', '#98c379', '#e5a44b', '#c678dd', '#56b6c2', '#e06c9f', '#7f8cd6'];
function colorFor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR[h % AVATAR.length]!;
}

function cleanDomain(d?: string, url?: string): string {
  let dom = (d || '').replace(/^www\./, '');
  if (!dom && url) { try { dom = new URL(url).hostname.replace(/^www\./, ''); } catch { /* keep '' */ } }
  return dom;
}

/** "Today" / "Yesterday" / "27 Jul" from an ISO date — the news-app date feel. */
function relDate(iso?: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const day = (ms: number) => { const d = new Date(ms); return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()); };
  const diff = Math.round((day(Date.now()) - day(t)) / 86_400_000);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const d = new Date(t);
  return `${d.getUTCDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()]}`;
}

export function SourceCards({ sources }: { sources: SourceItem[] }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="src-cards">
      <div className="src-cards__head">{sources.length} {sources.length === 1 ? 'Source' : 'Sources'}</div>
      <div className="src-cards__row">
        {sources.map((s) => {
          const dom = cleanDomain(s.domain, s.url);
          const date = relDate(s.publishedAt);
          return (
            <a
              key={`${s.n}-${s.url ?? dom}`}
              className="src-card"
              href={s.url}
              target="_blank"
              rel="noreferrer"
              title={s.title || dom}
            >
              <div className="src-card__top">
                <span className="src-card__fav" style={{ background: colorFor(dom || String(s.n)) }} aria-hidden="true">
                  {(dom || '?').charAt(0).toUpperCase()}
                </span>
                <span className="src-card__dom">{dom || 'source'}</span>
                <span className="src-card__n" aria-hidden="true">{s.n}</span>
              </div>
              <div className="src-card__title">{s.title || dom || s.url}</div>
              {date ? <div className="src-card__date">{date}</div> : null}
            </a>
          );
        })}
      </div>
    </div>
  );
}
